// Peças dos gráficos do estoque (recharts, já usado na plataforma).
// Paleta: 3 primeiros slots categóricos da referência (azul, laranja, verde-água), validados
// com o validador de paleta contra as superfícies deste módulo — claro #FFFFFF e escuro #1A1030,
// todos os pares (CVD ΔE ≥ 9,2; visão normal ≥ 20,9). No claro o verde-água fica abaixo de 3:1
// de contraste → todo gráfico tem legenda + visão em tabela (alívio exigido).
// Cor segue a entidade (a categoria mantém a cor mesmo filtrada), nunca a posição.
import { useState } from "react";
import { BarChart3, Table2 } from "lucide-react";

export function temaGrafico(isLight) {
  return isLight
    ? {
        series: ["#2a78d6", "#eb6834", "#1baf7a"],
        abc: { A: "#1c5cab", B: "#3987e5", C: "#86b6ef" }, // rampa ordinal azul (550/400/250)
        grid: "#ECE8F4", eixo: "#D6D0E4", tinta: "#1A1030", tinta2: "#52514e", muda: "#7C6FA0", superficie: "#FFFFFF",
      }
    : {
        series: ["#3987e5", "#d95926", "#199e70"],
        abc: { A: "#86b6ef", B: "#3987e5", C: "#1c5cab" },
        grid: "#2A2045", eixo: "#3A2E5C", tinta: "#EDE9FA", tinta2: "#c3c2b7", muda: "#9585C0", superficie: "#1A1030",
      };
}

const fmtBRLCompacto = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", notation: "compact", maximumFractionDigits: 1 });
export const brlCompacto = (v) => fmtBRLCompacto.format(Number(v) || 0);

export function eixoProps(T) {
  return {
    tick: { fill: T.muda, fontSize: 11 },
    axisLine: { stroke: T.eixo },
    tickLine: false,
  };
}

/** Legenda em cor de texto; o marcador colorido carrega a identidade. */
export function Legenda({ T, itens }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", fontSize: 12, color: T.tinta2, marginBottom: 6 }}>
      {itens.map((i) => (
        <span key={i.rotulo} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: i.linha ? 14 : 10, height: i.linha ? 2 : 10, borderRadius: i.linha ? 1 : 3, background: i.cor }} />
          {i.rotulo}
        </span>
      ))}
    </div>
  );
}

/** Tooltip no padrão: título + linhas "marcador · rótulo · valor", valores em tinta de texto. */
export function CaixaTooltip({ T, titulo, linhas }) {
  return (
    <div style={{ background: T.superficie, border: `1px solid ${T.eixo}`, borderRadius: 8, padding: "8px 10px", boxShadow: "0 6px 20px rgba(0,0,0,0.15)", fontSize: 12, color: T.tinta, minWidth: 150 }}>
      {titulo && <div style={{ fontWeight: 700, marginBottom: 4 }}>{titulo}</div>}
      {linhas.map((l) => (
        <div key={l.rotulo} style={{ display: "flex", alignItems: "center", gap: 6, lineHeight: 1.6 }}>
          {l.cor && <span style={{ width: 8, height: 8, borderRadius: 2, background: l.cor, flexShrink: 0 }} />}
          <span style={{ color: T.tinta2, flex: 1 }}>{l.rotulo}</span>
          <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{l.valor}</span>
        </div>
      ))}
    </div>
  );
}

/** Card de gráfico com alternância para a tabela equivalente (todo gráfico tem a sua). */
export function CardGrafico({ C, titulo, subtitulo, acoes, tabela, altura = 260, children }) {
  const [modo, setModo] = useState("grafico");
  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>{titulo}</h3>
          {subtitulo && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{subtitulo}</div>}
        </div>
        {acoes}
        <div role="group" aria-label="Visualização" style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
          {[["grafico", BarChart3, "Gráfico"], ["tabela", Table2, "Tabela"]].map(([id, Icone, rotulo]) => (
            <button key={id} onClick={() => setModo(id)} aria-pressed={modo === id} title={rotulo}
              style={{ display: "flex", alignItems: "center", padding: "6px 8px", border: "none", cursor: "pointer", background: modo === id ? C.bgActive : "transparent", color: modo === id ? C.brand : C.muted }}>
              <Icone size={15} />
            </button>
          ))}
        </div>
      </div>
      {modo === "grafico" ? <div style={{ width: "100%", height: altura }}>{children}</div> : (
        <div style={{ maxHeight: altura + 40, overflow: "auto" }}>{tabela}</div>
      )}
    </section>
  );
}

/** Tabela simples para a visão em tabela dos gráficos. colunas: [{id, rotulo, alinhar, formato}] */
export function TabelaDados({ C, colunas, linhas }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr>{colunas.map((c) => (
          <th key={c.id} style={{ padding: "6px 8px", textAlign: c.alinhar || "left", color: C.muted, fontSize: 11, textTransform: "uppercase", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card, whiteSpace: "nowrap" }}>{c.rotulo}</th>
        ))}</tr>
      </thead>
      <tbody>
        {linhas.map((l, idx) => (
          <tr key={idx}>{colunas.map((c) => (
            <td key={c.id} style={{ padding: "6px 8px", textAlign: c.alinhar || "left", borderBottom: `1px solid ${C.border}`, fontVariantNumeric: "tabular-nums", whiteSpace: c.alinhar === "right" ? "nowrap" : "normal" }}>
              {c.formato ? c.formato(l[c.id], l) : l[c.id]}
            </td>
          ))}</tr>
        ))}
        {linhas.length === 0 && <tr><td colSpan={colunas.length} style={{ padding: 16, textAlign: "center", color: C.muted }}>Sem dados.</td></tr>}
      </tbody>
    </table>
  );
}
